import { GRAPH_DEFAULTS } from "../Enums/index.js";
import dayjs from "dayjs";
import { Scale, Tick } from "chart.js";
import DocumentDO = App.Data.Explorer.DocumentDO;
import {
	horizontalDateGranularity,
	horizontalSupportsPeriodTicks,
	markIncompleteGranule,
	supportsGranularityMarking,
} from "../graphUtils.js";
import skipTicks, { getEffectiveSize } from "../Plugins/skipTicks.js";
import { PLUGIN_KEY } from "../Plugins/secondLevelLabels.js";
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import { isOldSnapshot } from "../Utils/DateUtils.js";
import { getChartConfig } from "./chartConfig.js";

export const DEFAULT_TICK_PADDING = 25;
// Matches the full height Chart.js allocates for the title: top padding + font line height (size * 1.2).
export const CONFIG_TEXT_HEIGHT = DEFAULT_TICK_PADDING + Math.ceil(12 * 1.2);

export enum HorizontalScaleRenderMode {
	DEFAULT,
	DATE_WITHOUT_YEAR,
	WEEK_WITHOUT_YEAR,
	MONTH_WITHOUT_YEAR,
	QUARTER_WITHOUT_YEAR,
}

// Copied from core.scale.autoskip.js
function determineMaxTicks(scale: Scale) {
	const offset = scale.options.offset;
	const tickLength = scale._tickSize();

	const maxScale = scale._length / tickLength + (offset ? 0 : 1);
	const maxChart = scale._maxLength / tickLength;

	return Math.floor(Math.min(maxScale, maxChart));
}

export function formatTickLabel(
	label: string,
	renderMode: HorizontalScaleRenderMode,
	maxDigits: number,
	derivedDocument: DocumentDO,
	options?: {
		isOffsetLabel?: boolean;
		isFirstOrLast?: boolean;
	}
): string {
	const rawLabel = String(label);
	let formatted = rawLabel;
	const { isOffsetLabel = false, isFirstOrLast = true } = options ?? {};

	const hadAsterisks =
		supportsGranularityMarking(derivedDocument) &&
		!isOffsetLabel &&
		isFirstOrLast &&
		markIncompleteGranule(
			formatted,
			derivedDocument.horizontal_dimension.granularity,
			derivedDocument
		);

	let year = "";
	switch (renderMode) {
		case HorizontalScaleRenderMode.DATE_WITHOUT_YEAR:
			const parsedLabel = dayjs(label);
			formatted = parsedLabel.format("DD MMM");
			year = parsedLabel.format("YYYY");
			break;

		case HorizontalScaleRenderMode.WEEK_WITHOUT_YEAR: {
			const labelParts = label.split("-");
			year = labelParts[0] ?? "";
			const weekNum = labelParts[1] ?? label;
			formatted = `W${weekNum}`;
			break;
		}

		case HorizontalScaleRenderMode.MONTH_WITHOUT_YEAR: {
			const labelParts = label.split("-");
			year = labelParts[0] ?? "";
			const monthNum = labelParts[1] ?? label;
			formatted = dayjs(monthNum, "MM").format("MMM");
			break;
		}

		case HorizontalScaleRenderMode.QUARTER_WITHOUT_YEAR:
			const labelParts = label.split("-");
			year = labelParts[0] ?? "";
			formatted = labelParts[1] ?? label;
			break;

		case HorizontalScaleRenderMode.DEFAULT:
		default:
			if (label.length > maxDigits) {
				formatted = label.substring(0, maxDigits);
			}
			break;
	}

	const invalidDate = "Invalid Date";
	if (formatted === invalidDate || year === invalidDate) {
		return rawLabel;
	}

	if (
		renderMode !== HorizontalScaleRenderMode.DEFAULT &&
		derivedDocument.is_horizontal_sorting_enabled
	) {
		formatted += ` ${year}`;
	}
	if (hadAsterisks) formatted += "*";

	return formatted;
}

export const xScale = (
	derivedDocument: DocumentDO,
	isDarkMode: boolean,
	renderMode: HorizontalScaleRenderMode = HorizontalScaleRenderMode.DEFAULT,
	hasOffsetAxis: boolean = false,
	showConfigurationText: boolean = true,
	showOnlyOldSnapshotDate: boolean = false,
	hierarchyNodes: Record<number, ActiveFilterDTO> = {}
) => {
	const isOldSnap = isOldSnapshot(
		dayjs(derivedDocument.period_measurement_parsed)
	);

	const hideSnapshotText = showOnlyOldSnapshotDate && !isOldSnap;

	const minRotation = () => {
		if (
			derivedDocument.offset_dimension !== null ||
			!derivedDocument.is_horizontal_label_rotation_enabled
		) {
			return 0;
		}
		return derivedDocument.horizontal_label_rotation_degrees * -1;
	};

	return {
		title: {
			font: {
				size: 12,
			},
			display: true,
			padding: function (scale) {
				// I don't know exactly why this works.
				// The tick padding is applied to both the top and the bottom leading to a lot empty
				// space below the horizontal labels.
				// By using -scale.chart[PLUGIN_KEY].maxHeight (which is the offset height) this space goes away.
				// We add DEFAULT_TICK_PADDING to add some additional padding such that the filter description has some room.
				return {
					top: hasOffsetAxis
						? -scale.chart[PLUGIN_KEY].maxHeight + DEFAULT_TICK_PADDING
						: hideSnapshotText
						? 0
						: DEFAULT_TICK_PADDING,
				};
			},
			text: (context) => {
				const tokens = getChartConfig(
					derivedDocument,
					showConfigurationText,
					hierarchyNodes,
					context
				);
				context.chart.$chartDescription = tokens;
				// Pass empty strings so chart.js reserves padding for the lines
				// while the chartDescription plugin renders the tokenized content.
				return tokens.map(() => "");
			},
			color: isDarkMode
				? GRAPH_DEFAULTS.TEXT_COLOR_DARK
				: GRAPH_DEFAULTS.TEXT_COLOR,
			align: "start",
		},
		grid: {
			// offset: false,
			color: GRAPH_DEFAULTS.LINE_COLOR,
		},
		position: "left",
		ticks: {
			padding: function (scale) {
				return hasOffsetAxis ? scale.chart[PLUGIN_KEY].maxHeight : 4;
			},
			color: isDarkMode
				? GRAPH_DEFAULTS.TEXT_COLOR_DARK
				: GRAPH_DEFAULTS.TEXT_COLOR,
			font: {
				size: 12,
			},
			autoSkip: false,
			autoSkipPadding: 5,
			// maxTicksLimit: 12,
			major: { enabled: false },
			maxRotation: 0,
			minRotation,
			callback(value, index, ticks) {
				const label = this.getLabelForValue(value);

				const isFirst = index === 0;
				const isLast = index === ticks.length - 1;
				const isFirstOrLast = isFirst || isLast;
				return formatTickLabel(
					label,
					renderMode,
					this.$maxTickDigits,
					derivedDocument,
					{
						isFirstOrLast: isFirstOrLast,
					}
				);
			},
		},
		beforeTickToLabelConversion(scale) {
			// Reset rotation via minRotation and labelRotation and remove maxTickDigits.
			// Doing this here will result in chartjs generating the labels at full length.
			// We need them at full length before the next step (beforeCalculateLabelRotation)
			scale.options.ticks.minRotation = minRotation;
			scale.labelRotation = minRotation();
			delete scale.$maxTickDigits;
		},
		beforeCalculateLabelRotation(scale) {
			scale.chart.options.plugins.yearLabels.offsetY = 20;
			scale.$ticksAreLimited = false;
			scale.options.grid.drawOnChartArea = false;

			const rotation = minRotation();

			// Set the maximum number of digits that can be shown.
			// scale._maxDigits doesn't take rotation into account.
			// If the label is rotated it takes less horizontal space.
			scale.$maxTickDigits = Math.ceil(
				scale._maxDigits() /
					Math.cos(rotation * (Math.PI * 180)) /
					scale.ticks.length
			);

			const maxTicks = determineMaxTicks(scale);
			if (maxTicks >= scale.ticks.length) {
				return;
			}

			const ticksToFilter = {};

			// Only apply special date logic when no sorting is enabled.
			const appliedLogic =
				!derivedDocument.is_horizontal_sorting_enabled &&
				skipTicks<Tick>(
					scale.ticks,
					(tick) => scale.getLabelForValue(tick.value),
					(tick, keep, isTime) => {
						if (isTime) {
							ticksToFilter[tick.value] = keep;

							return;
						}

						tick._keep = keep;
						if (!tick._keep) {
							tick.label = "";
						}
					},
					maxTicks,
					horizontalDateGranularity(derivedDocument),
					horizontalSupportsPeriodTicks(derivedDocument)
				);

			if (appliedLogic) {
				scale.ticks = scale.ticks.filter((tick) => {
					if (ticksToFilter[tick.value] !== undefined) {
						return ticksToFilter[tick.value];
					}

					return true;
				});

				// The time logic has filtered
				scale.$ticksAreLimited = true;

				return;
			}

			const fontWidth = scale._resolveTickFontOptions(0).lineHeight;
			const rotatedFontWidth = getEffectiveSize(
				fontWidth,
				fontWidth,
				rotation
			).effectiveWidth;

			const tickSize = scale.width / scale.chart.data.labels.length;

			// For labels rotated 90 degrees (vertical), shortening them won't result in
			// a reduced width. In this case we can't do anything.
			if (rotation >= 90) {
				return;
			}

			// If less than 5 characters are available we want to rotate the labels to get
			// some more available width. Only apply this when the offset labels are not used as these should be rotated instead.
			let forcedRotation = false;
			if (scale.$maxTickDigits < 5 && !hasOffsetAxis) {
				scale.options.ticks.minRotation = 45;
				forcedRotation = true;
				scale.chart.options.plugins.yearLabels.offsetY = 30;
				scale.$maxTickDigits *= 50;
			}

			// Regenerate tickLabels as we have computed new max digits.
			scale.generateTickLabels(scale.ticks);
			scale.configure();

			// If the tick size is less than the approximate width of a label. Then we want to keep only 1 tick every other X.
			// The approximate width depends on if the label is rotated. If it's rotated it's just 1 character.
			// If it's not then it's $maxTickDigits * width.
			const approxWidth =
				rotatedFontWidth *
				(rotation === 0 && !forcedRotation
					? Math.max(scale.ticks[0].label.toString().length, 1) * 0.8
					: 1); // the font width without rotation is too big. Using 0.8 seems to work.

			const amountOfTicksToCull = Math.ceil(approxWidth / tickSize);

			scale.ticks = scale.ticks.filter((tick, idx) => {
				tick._keep =
					idx % amountOfTicksToCull === 0 && amountOfTicksToCull !== Infinity;
				if (!tick._keep) {
					tick.label = "";
				}
				return tick;
			});
		},
	};
};
